import React, { useEffect, useState } from "react";
import { fetchMonthlyThroughputPerHour } from "../api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from '@mui/material/styles';
import { Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";
import { parseLocalDateTime } from "../utils/localDateTime";

const stddev = arr => {
  const n = arr.length;
  if (n === 0) return 0;
  const mean = arr.reduce((a, b) => a + b, 0) / n;
  return Math.sqrt(arr.reduce((a, b) => a + (b - mean) ** 2, 0) / n);
};

const CustomTooltip = ({ active, payload, label, formatNumber, naLabel }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #ccc', padding: 12 }}>
      <div>{label}</div>
      {payload.map((entry, i) => (
        <div key={i} style={{ color: entry.color, fontWeight: entry.dataKey === 'Total' ? 'bold' : 'normal' }}>
          {entry.name} : {entry.value != null ? formatNumber(entry.value) : naLabel}
        </div>
      ))}
    </div>
  );
};

const MonthlyThroughputPerHourChart = ({ date, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  const formatNumber2 = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return t("common.na");
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  useEffect(() => {
    if (!date) return setData([]);
    onLoadingChange?.(true);
    fetchMonthlyThroughputPerHour(date)
      .then(setData)
      .catch(() => setData([]))
      .finally(() => onLoadingChange?.(false));
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  if (data.length === 0) return <div>{t("common.noData")}</div>;
  // Pivot data: one line per workcell_name, plus Total
  const days = [...new Set(data.map(d => d.day))];
  const workcells = [...new Set(data.filter(d => d.workcell_id !== null).map(d => d.workcell_name))];
  // Map workcell name to ID for URL usage
  const workcellNameToId = {};
  data.forEach(d => {
    if (d.workcell_id !== null && d.workcell_name) {
      workcellNameToId[d.workcell_name] = d.workcell_id;
    }
  });
  const chartData = days.map(day => {
    const row = { day };
    data.filter(d => d.day === day).forEach(d => {
      // Set to null if throughput is zero, to break the line
      row[d.workcell_name] = d.throughput === 0 ? null : d.throughput;
      if (d.workcell_id === null) row["Total"] = d.throughput === 0 ? null : d.throughput;
    });
    return row;
  });
  // Calculate averages and stddevs
  const stats = {};
  workcells.forEach((name, i) => {
    const vals = chartData.map(row => row[name]).filter(v => v != null);
    stats[name] = {
      avg: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
      std: vals.length ? stddev(vals) : null,
      color: theme.palette.chart?.colors?.[i % (theme.palette.chart.colors.length)] || theme.palette.primary.main
    };
  });
  const totalVals = chartData.map(row => row["Total"]).filter(v => v != null);
  stats["Total"] = {
    avg: totalVals.length ? totalVals.reduce((a, b) => a + b, 0) / totalVals.length : null,
    std: totalVals.length ? stddev(totalVals) : null,
    color: "#000"
  };
  // Add avg lines as constant arrays
  const avgLines = [
    <Line key="avg-total" type="monotone" dataKey={() => stats["Total"].avg} stroke="#000" strokeWidth={2} dot={false} strokeDasharray="6 6" name={t("monthly.throughputPerHour.chart.avgTotal")} legendType="none" />,
    ...workcells.map((name, i) => (
      <Line
        key={`avg-${name}`}
        type="monotone"
        dataKey={() => stats[name].avg}
        stroke={stats[name].color}
        strokeWidth={2}
        dot={false}
        strokeDasharray="6 6"
        name={t("monthly.throughputPerHour.chart.avg", { name })}
        legendType="none"
      />
    ))
  ];
  // Custom tick for month name (only show for first day of month)
  const MonthTick = (props) => {
    const { x, y, payload, index } = props;
    const date = parseLocalDateTime(payload.value);
    const isFirst = index === 0;
    const prevDate = index > 0 ? parseLocalDateTime(chartData[index - 1].day) : null;
    const isMonthStart = isFirst || (prevDate && prevDate.getMonth() !== date.getMonth());
    return isMonthStart ? (
      <g>
        <text x={x} y={y + 16} textAnchor="middle" fontSize={12} fill="#888">
          {date.toLocaleString(locale, { month: 'long' })}
        </text>
      </g>
    ) : null;
  };

  return (
    <>
      <ResponsiveContainer width="100%" height={450}>
        <LineChart data={chartData}>
          {/* Main X axis: days */}
          <XAxis
            dataKey="day"
            tickFormatter={d => parseLocalDateTime(d)?.getDate() || ''}
            height={30}
            axisLine={true}
            tickLine={true}
          />
          {/* Secondary X axis: month name */}
          <XAxis
            dataKey="day"
            axisLine={false}
            tickLine={false}
            height={20}
            xAxisId="month"
            tick={MonthTick}
            interval={0}
            allowDataOverflow={true}
            orientation="bottom"
          />
          <YAxis />
            <Tooltip content={<CustomTooltip formatNumber={formatNumber2} naLabel={t("common.na")} />} />
            <Line type="monotone" dataKey="Total" stroke="#000" strokeWidth={3} dot={false} name={t("monthly.throughputPerHour.chart.totalThroughput")} />
          {workcells.map((name, i) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={stats[name].color}
              strokeWidth={2}
              dot={false}
              name={name}
            />
          ))}
          {avgLines}
        </LineChart>
      </ResponsiveContainer>
      <Table sx={{ mt: 2, maxWidth: 900, mx: 'auto' }} size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("monthly.throughputPerHour.table.workcell")}</TableCell>
            <TableCell align="right">{t("monthly.throughputPerHour.table.avgThroughput")}</TableCell>
            <TableCell align="right">{t("monthly.throughputPerHour.table.stddev")}</TableCell>
            <TableCell sx={{ width: '10rem' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={{ fontWeight: 'bold', color: '#000', backgroundColor: theme.palette.action.hover }}>
            <TableCell component="th" scope="row" sx={{ color: '#000', fontWeight: 'bold' }}>{t("common.total")}</TableCell>
            <TableCell align="right">{stats["Total"].avg != null ? formatNumber2(stats["Total"].avg) : t("common.na")}</TableCell>
            <TableCell align="right">{stats["Total"].std != null ? formatNumber2(stats["Total"].std) : t("common.na")}</TableCell>
            <TableCell sx={{ width: '10rem' }} />
          </TableRow>
          {workcells.map((name, i) => (
            <TableRow key={name}>
              <TableCell component="th" scope="row" sx={{ color: stats[name].color, fontWeight: 'bold' }}>{name}</TableCell>
              <TableCell align="right">{stats[name].avg != null ? formatNumber2(stats[name].avg) : t("common.na")}</TableCell>
              <TableCell align="right">{stats[name].std != null ? formatNumber2(stats[name].std) : t("common.na")}</TableCell>
              <TableCell align="right">
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate(`/chart/workcell/${workcellNameToId[name]}/monthly/${date}`)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t("common.viewDetails")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default MonthlyThroughputPerHourChart;
